// Browser-facing Shows service base URL — same env-driven, no-static-
// fallback convention as every other domain's config.ts.
export const SHOWS_API_BASE_URL = process.env.NEXT_PUBLIC_SHOWS_API_URL || '';


/** The public-facing route prefix for the whole Shows domain — every internal Link/redirect
 *  inside `app/shows/*` builds its target off THIS, never a bare `'/shows'` literal, so the
 *  address bar shows `/shows/...` throughout the listing + booking funnel. This matches the real
 *  Next.js route tree, which is physically rooted at `app/shows/` — no rewrite is involved on the
 *  happy path. `next.config.js` keeps a `/new-shows/:path* -> /shows/:path*` rewrite purely as a
 *  LEGACY alias, so old links/bookmarks/indexed URLs and already-sent emails still resolve. Two
 *  files OUTSIDE this domain (`app/_components/NavbarLinks.tsx`,
 *  `app/_components/nearby/cards/NearbyShowCard.tsx`) build the SAME `/shows` path as a hardcoded
 *  literal rather than importing this — cross-domain `_components` imports are a hard rule
 *  violation (route isolation), so keep those two in sync with this constant by hand if it ever
 *  changes. */
export const SHOWS_BASE_PATH = '/shows';

// Category filter options are ENTIRELY data-driven — `ShowsFilters.tsx` builds them straight from
// `facets.categories` (only categories with at least one matching show, per the backend's
// `buildFacets()`), never from a static list. There used to be a hardcoded 21-entry fallback list
// here that was UNIONED into the options regardless of live data, which meant every one of those
// names always rendered (with a 0 count) even with zero matching shows — removed per explicit
// requirement ("only show categories that have corresponding shows").

// Rating bands offered by the filter. There is deliberately NO "5+" option (removed per product:
// the list tops out at "4+", which is where 5.0-rated shows now land) — matching the CMS Rating
// dropdown in `theaters-cms-reference-code/pages/Shows.html`, which lists 4+/3+/2+ only.
//
// The TOP band is cumulative and every band below it is discrete — see `ratingInBucket` in
// `shows-search.service.ts` (backend) and `ShowsFilters.tsx` (frontend label/chip logic):
// 4 → [4.0, 5.0] (includes exactly 5.0), 3 → [3.0, 4.0), 2 → [2.0, 3.0), 1 → [1.0, 2.0).
// Like categories, an option only renders when the current facet count for its band is > 0
// (dynamic, not a static always-shown list) — see `ShowsFilters.tsx`'s `ratingOptions`.
export const RATING_THRESHOLDS = [4, 3, 2, 1] as const;

// brd-backend.md §3.2 documented default slider bounds.
export const DEFAULT_PRICE_MIN = 200;
export const DEFAULT_PRICE_MAX = 106200;

export type ShowsSortOption = 'recommended' | 'price' | 'discount' | 'rating' | 'newest';

// The live `theaters-cms-reference-code/pages/Shows.html` sort dropdown
// (`#topSellingDropdown`) actually only lists 4 items — no "By Discount"
// entry — with these exact labels ("Highest Rated", not "Highest Rating").
// This is stronger, more direct evidence for the open contradiction already
// flagged in DOCS/shows/listing/frd.md gap #1 (the "By Discount" sorter is
// fully spec'd in brd-backend.md §2.2 but noted "to be removed"): the real
// page's own markup confirms it was in fact left out of the UI. Backend
// support for `sortBy=discount` is kept (harmless, already built) — it's
// just not surfaced as an option here, matching the live page verbatim.
export const SORT_OPTIONS: { value: ShowsSortOption; label: string; indicator: string }[] = [
  { value: 'recommended', label: 'Our Recommendations', indicator: 'Sorted by : Our Recommendations' },
  { value: 'price', label: 'Price: Low to High', indicator: 'Sorted by : Price: Low to High' },
  { value: 'rating', label: 'Highest Rated', indicator: 'Sorted by : Highest Rated' },
  { value: 'newest', label: 'Newest Events', indicator: 'Sorted by : Newest Events' },
];

/** IANA time zone of the browser — sent to the backend so Today/This
 *  Weekend resolve against the user's actual local day (brd-backend.md
 *  §3.5/§3.6). Only callable client-side. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}
